import { Link } from 'react-router-dom';
import { Crown, Lock, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Locked Preview Component for Pro-only features
export const LockedPreview = ({ 
  feature, 
  previewContent, 
  isLocked = true,
  planRequired = 'Pro'
}) => {
  if (!isLocked) return null;

  return (
    <div className="relative">
      {/* Preview content with blur */}
      <div className="relative">
        <div className="pointer-events-none select-none">
          {previewContent}
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
      </div>
      
      {/* Locked overlay */}
      <div className="mt-2 p-4 rounded-lg bg-neutral-900/80 border border-primary/20">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-neutral-300">
            Upgrade to {planRequired} to unlock
          </span>
        </div>
        <p className="text-xs text-neutral-500 mb-3">
          Preview all AI outputs. Upgrade to copy, export, and generate more.
        </p>
        <Link to="/pricing">
          <Button size="sm" className="btn-primary text-xs">
            <Crown className="w-3 h-3 mr-1" />
            Upgrade to {planRequired}
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Pro Badge component
export const ProBadge = ({ plan = 'Pro', small = false }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/20 text-primary font-medium ${small ? 'text-[10px]' : 'text-xs'}`}>
    <Crown className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
    {plan}
  </span>
);

// Locked Copy Button - shows tooltip on hover
export const LockedCopyButton = ({ isLocked = false, onClick, copied, copyKey }) => {
  if (!isLocked) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="btn-ghost text-xs"
        onClick={onClick}
      >
        {copied === copyKey ? (
          <>
            <span className="w-3 h-3 mr-1 text-emerald-500">✓</span>
            Copied
          </>
        ) : (
          <>
            <Copy className="w-3 h-3 mr-1" />
            Copy
          </>
        )}
      </Button>
    );
  }

  return (
    <div className="relative group">
      <Button
        variant="ghost"
        size="sm"
        className="btn-ghost text-xs opacity-50 cursor-not-allowed"
        disabled
      >
        <Lock className="w-3 h-3 mr-1" />
        Copy
      </Button>
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-neutral-800 rounded-lg text-xs text-neutral-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
        Upgrade to unlock
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-800" />
      </div>
    </div>
  );
};

// Sample preview data for locked features
export const samplePreviewData = {
  bestQuotes: [
    {
      quote: "This is an example of how best quotes appear with speaker attribution...",
      speaker: "Guest Name",
      context: "Discussion topic"
    },
    {
      quote: "Another powerful quote that would be extracted from your episode...",
      speaker: "Host",
      context: "Key insight"
    }
  ],
  seoTitles: [
    "How to [Topic] - A Complete Guide for [Audience]",
    "The Ultimate [Topic] Strategy That Actually Works",
    "[Number] Ways to Improve Your [Topic] Today",
    "Why [Topic] Matters More Than You Think",
    "The Hidden Secret Behind Successful [Topic]"
  ],
  tiktokCaption: {
    hook: "This is what your TikTok hook would look like 👇",
    caption: "Your episode's key insights transformed into engaging TikTok content with proper formatting...",
    hashtags: "#podcast #content #viral #fyp #business",
    cta: "Watch to learn more 🚀"
  },
  instagramCaptions: {
    carousel_caption: "Your carousel caption would appear here with proper formatting for Instagram engagement...",
    story_caption: "Story text optimized for quick reads",
    reels_caption: "Reels caption with hook and value..."
  }
};

export default LockedPreview;
